import { isValid, parseISO } from "date-fns";
import type { PayLinkRowResponse } from "../services/payLinkService";
import { formatCurrencyValue } from "./numberFormatter";
import type {
  PaymentLinkRow,
  PaymentLinkStatus,
} from "../types/paymentLinksPanel";

function parseApiAmount(raw: PayLinkRowResponse["amount"]): number {
  if (raw == null) {
    return 0;
  }

  if (typeof raw === "number" && Number.isFinite(raw)) {
    return raw;
  }

  if (typeof raw === "string") {
    const parsed = parseFloat(raw);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  if (
    typeof raw === "object" &&
    raw !== null &&
    "$numberDecimal" in raw &&
    typeof raw.$numberDecimal === "string"
  ) {
    const parsed = parseFloat(raw.$numberDecimal);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  return 0;
}

function formatAmountDisplay(amount: number, currency: string): string {
  const normalizedCurrency = String(currency || "").trim();
  const code = /^[a-f\d]{24}$/i.test(normalizedCurrency)
    ? "ZAR"
    : (normalizedCurrency || "ZAR").toUpperCase();
  return formatCurrencyValue(amount, code);
}

function resolveDisplayStatus(
  apiStatus: string | undefined,
  expiresAtIso: string | undefined
): PaymentLinkStatus {
  const normalizedStatus = (apiStatus || "").toLowerCase();

  if (
    normalizedStatus === "paid" ||
    normalizedStatus === "confirmed" ||
    normalizedStatus === "completed"
  ) {
    return "paid";
  }

  if (normalizedStatus === "expired" || normalizedStatus === "cancelled") {
    return "expired";
  }

  if (expiresAtIso) {
    const expiryDate = parseISO(expiresAtIso);
    if (isValid(expiryDate) && expiryDate.getTime() <= Date.now()) {
      return "expired";
    }
  }

  return "unpaid";
}

function resolveDisplayName(row: PayLinkRowResponse): string {
  const recipientName = row.recipientName?.trim() || "";
  const recipientSurname = row.recipientSurname?.trim() || "";
  const fullName = `${recipientName} ${recipientSurname}`.trim();

  if (fullName) {
    return fullName;
  }

  if (row.description?.trim()) {
    return row.description.trim();
  }

  return row.payLinkId || row._id || "Payment link";
}

function formatCreatedLabel(createdAt?: string): string {
  if (!createdAt) {
    return "—";
  }

  const date = parseISO(createdAt);
  if (!isValid(date)) {
    return "—";
  }

  const dd = String(date.getUTCDate()).padStart(2, "0");
  const mm = String(date.getUTCMonth() + 1).padStart(2, "0");
  const yyyy = date.getUTCFullYear();
  return `${dd}/${mm}/${yyyy}`;
}

export function mapApiPayLinkToRow(row: PayLinkRowResponse): PaymentLinkRow {
  const amountNumeric = parseApiAmount(row.amount);
  const createdAt = row.createdAt;
  const createdAtMs = createdAt
    ? (() => {
        const createdDate = parseISO(createdAt);
        return isValid(createdDate) ? createdDate.getTime() : undefined;
      })()
    : undefined;

  return {
    id: row.payLinkId || row._id || createdAt || "paylink",
    backendId: row._id,
    name: resolveDisplayName(row),
    createdLabel: formatCreatedLabel(createdAt),
    createdAt,
    createdAtMs,
    amountFormatted: formatAmountDisplay(amountNumeric, row.currency || "ZAR"),
    amountNumeric,
    reference: row.reference?.trim() || row.description?.trim() || "—",
    paymentLinkUrl: row.payLinkUrl,
    paymentMethod: row.paymentMethod === "crypto" ? "crypto" : "88pay",
    shareUrl: row.payLinkUrl,
    status: resolveDisplayStatus(row.status, row.expiresAt),
  };
}
